import { useEffect, useState } from 'react';

export type ScriptStatus = 'idle' | 'loading' | 'ready' | 'error';

const getScriptStatus = (scriptId: string): ScriptStatus => {
  const script = document.getElementById(scriptId) as HTMLScriptElement | null;
  if (!script) return 'idle';
  const current = script.getAttribute('data-status');
  if (current === 'ready' || current === 'error' || current === 'loading') return current;
  return 'idle';
};

export const useScript = (src: string, scriptId: string): ScriptStatus => {
  const [status, setStatus] = useState<ScriptStatus>(() => (src ? getScriptStatus(scriptId) : 'idle'));

  useEffect(() => {
    if (!src) {
      setStatus('idle');
      return;
    }

    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = src;
      script.async = true;
      script.defer = true;
      script.setAttribute('data-status', 'loading');
      document.head.appendChild(script);
    }

    const setReady = () => {
      script?.setAttribute('data-status', 'ready');
      setStatus('ready');
    };
    const setError = () => {
      script?.setAttribute('data-status', 'error');
      setStatus('error');
    };

    const currentStatus = script.getAttribute('data-status') as ScriptStatus | null;
    if (currentStatus === 'ready' || currentStatus === 'error') {
      setStatus(currentStatus);
      return;
    }

    setStatus('loading');
    script.addEventListener('load', setReady);
    script.addEventListener('error', setError);

    return () => {
      script?.removeEventListener('load', setReady);
      script?.removeEventListener('error', setError);
    };
  }, [scriptId, src]);

  return status;
};
